import React, { useState, useEffect, useMemo } from "react"
import { AxiosError } from "axios"
import { Layout, Table, Row, Col, Divider, Breadcrumb } from "antd"
import { LayoutPageContent, LayoutPageHeader } from "components"
import { RouteComponentProps } from "react-router"
import { FileTextOutlined, PlusOutlined } from "@ant-design/icons"
import { QUOTE_COLUMNS } from "./constants"
import { useConfigurationContext } from "contexts/configuration/context"
import { IPortalQuoteTable } from "models/quotes/portalQuoteTable"
import { SearchComponent, FilterComponent } from "./components"
import useOpus from "contexts/opus/hooks/use-opus"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import {
  useOpusPermissions,
  EOpusPermission
} from "contexts/authorization/hooks/use-opus-permissions"
import { EUserGroups } from "platform-client/types/user"
import usePlatform from "contexts/platform/use-platform"
import { getUserTokenValue } from "utils/pocketknife/get-user-token-value"

const NewQuoteButton = () => {
  const platform = usePlatform()
  const userGroupType = getUserTokenValue("grouptype", platform.token || "")
  const configuration = useConfigurationContext()
  const { havePermission: canUseNewQuote } = useOpusPermissions(
    EOpusPermission.Quote,
    "create"
  )
  const { havePermission: canManageBrokerQuotes } = useOpusPermissions(
    EOpusPermission.BrokerQuoteManagement,
    "true"
  )

  if (!canUseNewQuote || configuration.flags.platformVersion === "v3") {
    return null
  }

  const newQuoteTo =
    canManageBrokerQuotes && userGroupType === "Distributor"
      ? "/client-quote/create"
      : "/choose-quote"

  return (
    <Link to={newQuoteTo} className="ant-btn ant-btn-primary">
      <PlusOutlined /> New Quote
    </Link>
  )
}

export const AllQuotes: React.FunctionComponent<IAllQuotes> = (props) => {
  const { quoteClient } = useOpus()
  const platform = usePlatform()

  const configuration = useConfigurationContext()

  const [quotesType, setQuotesTypes] = useState<
    { value: string; label: string; key: string } | undefined
  >(undefined)

  const [quotesData, setQuotesData] = useState<IPortalQuoteTable[] | []>([])
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string | undefined | null>(
    undefined
  )

  const { data: currentUser } = useQuery(
    ["currentUser"],
    async () => platform.user.getCurrentUser(),
    {}
  )

  const enabledQuoteTypes = useMemo(() => {
    const baseQuoteTypes = configuration?.quote?.enabledQuoteTypes ?? []
    if (currentUser?.groupType !== EUserGroups.Distributor) {
      return baseQuoteTypes
    }

    return baseQuoteTypes.includes("MTA")
      ? baseQuoteTypes
      : [...baseQuoteTypes, "MTA"]
  }, [configuration?.quote?.enabledQuoteTypes, currentUser?.groupType])

  const { isFetching, refetch } = useQuery<
    IPortalQuoteTable[] | undefined,
    AxiosError
  >(
    ["quote-list", props.match.params.status, enabledQuoteTypes],
    async () =>
      quoteClient.getQuotes(enabledQuoteTypes, props.match.params.status),

    {
      refetchOnMount: "always",
      enabled:
        !!currentUser &&
        currentUser?.groupType !== undefined &&
        enabledQuoteTypes.length > 0,
      onSuccess: (quoteList) => {
        if (quoteList) {
          setQuotesData(quoteList)
        }
      }
    }
  )

  useEffect(() => {
    // Avoid triggering a refetch on initial mount where `searchValue` starts as `undefined`.
    // We only want to reset the list when the user explicitly clears the search (sets it to `null`).
    if (searchValue === null && currentUser?.groupType !== undefined) {
      refetch()
    }
  }, [searchValue, refetch, currentUser?.groupType])

  return (
    <Layout>
      <LayoutPageHeader
        ghost={false}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <FileTextOutlined />
              &nbsp; Quotes
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        title="All Quotes"
        extra={<NewQuoteButton />}
      />
      <LayoutPageContent>
        <Row justify="end" align="middle">
          <Col style={{ padding: 16 }}>
            Filter by status:
            <FilterComponent
              quotesType={quotesType}
              quoteStatus={props.match.params.status}
              quoteConfiguration={configuration?.quote}
              setQuotesTypes={setQuotesTypes}
            />
            <Divider style={{ height: 20 }} type="vertical" />
            <SearchComponent
              setSearchValue={setSearchValue}
              setSearchLoading={setSearchLoading}
              searchValue={searchValue}
              quotesUpdate={setQuotesData}
              quotesFilter={quotesType}
            />
          </Col>
        </Row>
        <Table
          scroll={{ x: "max-content" }}
          loading={isFetching || isSearchLoading}
          columns={QUOTE_COLUMNS(
            configuration?.quote?.types,
            configuration?.flags?.platformVersion
          )}
          dataSource={quotesData}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            hideOnSinglePage: true,
            position: ["bottomCenter"],
            size: "small"
          }}
        />
      </LayoutPageContent>
    </Layout>
  )
}

export type IAllQuotes = RouteComponentProps<{
  status?: string | undefined
}>
